import React, { useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { apiPost, planningPath } from '../../services/PlanningApiClient';
import { warningsFromSaveResponse, checkGuestContacts } from '../../services/GuestContactCheckService';
import { normalizeGuestName } from '../../utils/guestName';

export interface BulkAddSummary { created: number; failed: number; notes: string[] }

interface Row { name: string; email: string; phone: string; side: string }

/** One guest per line: "Name, email, phone, side" (comma or tab separated; only the name is required). */
export function parseGuestLines(text: string): Row[] {
  return text.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => {
    const cells = line.split(/\t|,/).map(c => c.trim());
    return { name: normalizeGuestName(cells[0] ?? ''), email: cells[1] ?? '', phone: cells[2] ?? '', side: (cells[3] ?? '').toLowerCase() };
  }).filter(row => row.name);
}

const newKey = () => `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired after every submit (even when some rows failed) so the caller can refresh and report. */
  onImported: (summary: BulkAddSummary) => void;
}

/**
 * Bulk add by pasting rows from a sheet. Each row is its own create call with its own idempotency key, so a retry
 * of the same batch never creates a guest twice. A phone or email another guest already uses is shown in the preview
 * and in the result; it never stops a row from being added.
 */
const BulkAddGuestsModal: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string[]>([]);
  const rows = useMemo(() => parseGuestLines(text), [text]);
  // one key per row position and content, so pressing the button twice resends the same keys
  const keys = useMemo(() => rows.map(newKey), [rows]);

  const checkContacts = async () => {
    const results = await checkGuestContacts(rows.map(r => ({ phone: r.phone || null, email: r.email || null })));
    setPreview(results.flatMap((warnings, i) => warnings.map(w => `${rows[i].name}: ${w.message}`)));
  };

  const submit = async () => {
    setBusy(true);
    const summary: BulkAddSummary = { created: 0, failed: 0, notes: [] };
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const saved = await apiPost<unknown>(planningPath('/guests'), {
          name: row.name, email: row.email, phone: row.phone || null, side: row.side || null,
          tags: [], status: 'active', rsvp_status: 'pending', plus_ones: [], idempotency_key: keys[i],
        });
        summary.created += 1;
        for (const w of warningsFromSaveResponse(saved)) summary.notes.push(`${row.name}: ${w.message}`);
      } catch (err) {
        summary.failed += 1;
        summary.notes.push(`${row.name}: ${err instanceof Error ? err.message : 'could not be added.'}`);
      }
    }
    setBusy(false);
    onImported(summary);
    if (summary.failed === 0) { setText(''); setPreview([]); onClose(); }
  };

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Bulk add guests" size="large" className="gst-bulk">
      <p className="form-helper">Paste one guest per line: Name, email, phone, side. Only the name is needed.</p>
      <textarea className="form-input gst-bulk__text" rows={10} value={text} aria-label="Guests to add"
        onChange={e => { setText(e.target.value); setPreview([]); }} placeholder="Ada Obi, ada@example.com, + country code and number, bride" />
      <p className="form-helper">{rows.length} guest{rows.length === 1 ? '' : 's'} ready to add.</p>
      {preview.length > 0 && (
        <ul className="gst-bulk__notes" aria-label="Phones and emails already used">
          {preview.map((p, i) => <li key={i}>{p}</li>)}
          <li>You can still add these guests.</li>
        </ul>
      )}
      <div className="gst-form__actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="outline" onClick={checkContacts} disabled={busy || rows.length === 0}>Check phones and emails</Button>
        <Button variant="primary" onClick={submit} loading={busy} disabled={rows.length === 0}>Add {rows.length || ''} guests</Button>
      </div>
    </Modal>
  );
};

export default BulkAddGuestsModal;
